import React, { useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import enviarConfirmacaoIcon from '../../../assets/wpp.png';
import UsuarioDrop from '../../droplist/UsuarioDrop';
import { EstadoCarregando, EstadoVazio } from './EstadosTabela';
import StatusPublicador from './StatusPublicador';
import ModalSubstituicao from './ModalSubstituicao';
import featureFlags from '../../../config/featureFlags';
import Snackbar from '../../common/Snackbar';

const ContainerTabela = styled.div.attrs(() => ({
  className: 'container-tabela',
}))`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0; /* permite flex children calcularem altura corretamente */
`;

const WrapperTabela = styled.div`
  width: 100%;
  flex: 1; /* ocupa todo espaço disponível */
  display: flex;
  flex-direction: column;
  background-color: white;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.1);
  border-radius: 8px 8px 0 0;
  margin-bottom: 3px; /* distância desejada do rodapé */
  min-height: 0; /* evita overflow inesperado no flex container */
  overflow: auto; /* Permite scroll em ambos os eixos na tabela inteira */
`;

const IconeSubstituirSvg = styled.svg`
  width: 17px; 
  height: 18px;
  margin-right: 6px;
  cursor: pointer;
  
  path {
     fill: #000000;
     transition: fill 0.2s;
  }
`;

const TabelaEstilizada = styled.div.attrs(() => ({
  className: 'tabela-estilizada',
}))`
  display: grid;
  grid-template-rows: auto auto;
  grid-template-columns:
    46px 110px minmax(160px, 1fr) minmax(160px, 1fr) minmax(150px, auto)
    minmax(110px, auto) 40px;
  width: 100%;
  background-color: white;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  border-radius: 8px 8px 0 0;

  @media (max-width: 1024px) {
    grid-template-columns:
      40px 90px minmax(120px, 1fr) minmax(120px, 1fr) minmax(120px, auto)
      minmax(90px, auto) 36px;
  }

  @media (max-width: 767px) {
    /* Mantém layout legível com scroll horizontal */
    min-width: 1000px;
    grid-template-columns:
      46px 110px minmax(160px, 1fr) minmax(160px, 1fr) minmax(150px, auto)
      minmax(110px, auto) 40px;
  }
`;

const CabecalhoTabela = styled.div.attrs(() => ({
  className: 'cabecalho-tabela',
}))`
  display: contents;
  background-color: #008080;
  color: white;
`;

const CabecalhoSticky = styled.div`
  display: grid;
  grid-template-columns:
    46px 110px minmax(160px, 1fr) minmax(160px, 1fr) 210px 120px 40px;
  background-color: #008080;
  color: white;
  position: sticky;
  top: 0;
  z-index: 10;
  box-shadow: 0 2px 2px rgba(0,0,0,0.08);
  width: 100%;

  @media (max-width: 1024px) {
    grid-template-columns:
      40px 90px minmax(120px, 1fr) minmax(120px, 1fr) 180px 100px 36px;
  }

  @media (max-width: 767px) {
    min-width: 1000px;
    grid-template-columns:
      46px 110px minmax(160px, 1fr) minmax(160px, 1fr) 210px 120px 40px;
  }
`;

const LinhaTabela = styled.div`
  display: grid;
  grid-template-columns:
    46px 110px minmax(160px, 1fr) minmax(160px, 1fr) 210px 120px 40px;
  align-items: stretch;
  min-height: 60px;
  width: 100%;
  &:nth-child(even) {
    background-color: #f9f9f9;
  }

  @media (max-width: 1400px) {
    grid-template-columns:
      42px 100px minmax(140px, 1fr) minmax(140px, 1fr) 190px 110px 38px;
    min-height: 55px;
  }

  @media (max-width: 1024px) {
    grid-template-columns:
      40px 90px minmax(120px, 1fr) minmax(120px, 1fr) 180px 100px 36px;
    min-height: 52px;
  }

  @media (max-width: 767px) {
    min-width: 1000px;
    grid-template-columns:
      46px 110px minmax(160px, 1fr) minmax(160px, 1fr) 210px 120px 40px;
    min-height: 48px;
  }
`;

const CelulaCabecalho = styled.div.attrs(() => ({
  className: 'celula-cabecalho',
}))`
  padding: 12px 10px;
  text-align: ${(props) => (props.center ? 'center' : 'left')};
  font-family: 'Roboto', sans-serif;
  font-size: 13px;
  font-weight: 600;
  border-bottom: 1px solid #007070;
  display: flex;
  align-items: center;
  justify-content: ${(props) => (props.center ? 'center' : 'flex-start')};
  background-color: #008080;
  letter-spacing: .2px;
  line-height: 1.25;
  word-break: break-word;

  @media (max-width: 1024px) {
    padding: 10px 8px;
    font-size: 12.5px;
  }

  @media (max-width: 767px) {
    padding: 8px 6px;
    font-size: 12px;
  }
`;

const CorpoTabela = styled.div`
  /* Removemos overflow e flex para que o scroll seja gerenciado pelo WrapperTabela */
  min-height: 0;
  width: 100%;
`;

const CelulaTabela = styled.div.attrs(() => ({ className: 'celula-tabela' }))`
  padding: 12px 10px;
  text-align: ${(props) => (props.center ? 'center' : 'left')};
  font-family: 'Roboto', sans-serif;
  font-size: 13px;
  font-weight: 400;
  border-bottom: 1px solid #e1e1e1;
  white-space: normal;
  word-break: break-word;
  display: flex;
  flex-direction: column;
  justify-content: ${(props) =>
    props.center || props.centerVertical ? 'center' : 'flex-start'};
  align-items: ${(props) => (props.center ? 'center' : 'flex-start')};
  min-height: 60px;
  line-height: 1.3;

  @media (max-width: 1024px) {
    padding: 10px 8px;
    font-size: 12.5px;
    min-height: 54px;
  }

  @media (max-width: 767px) {
    padding: 8px 6px;
    font-size: 12px;
    min-height: 50px;
  }
`;

const CaixaSelecao = styled.input.attrs(() => ({
  className: 'caixa-selecao',
  type: 'checkbox',
}))`
  cursor: pointer;
`;

const IconeOrdenacao = styled.svg.attrs(() => ({
  className: 'icone-ordenacao',
}))`
  width: 24px;
  height: 24px;
  cursor: pointer;
  margin-right: 3px;
`;

const CelulaIcone = styled.div.attrs(() => ({ className: 'celula-icone' }))`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 12px;
  cursor: pointer;
  color: red;
  border-bottom: 1px solid #ddd;
  min-height: 60px;
`;

const IconeLixeira = styled.svg.attrs(() => ({ className: 'icone-lixeira' }))`
  width: 32px;
  height: 32px;
  cursor: pointer;
  color: #db1212;
`;

const IconeConfirmado = styled.svg.attrs(() => ({
  className: 'icone-confirmado',
}))`
  width: 16px;
  height: 16px;
`;

const IconePendente = styled.svg.attrs(() => ({ className: 'icone-pendente' }))`
  width: 16px;
  height: 16px;
  stroke: #9d9d09;
  stroke-width: 1.5;
  cursor: pointer;
`;

const IconeCancelado = styled.svg.attrs(() => ({
  className: 'icone-cancelado',
}))`
  width: 16px;
  height: 16px;
  stroke: #a72424;
  stroke-width: 1.5;
  cursor: pointer;
`;

const IconeCanceladoCoordenador = styled.svg.attrs(() => ({
  className: 'icone-cancelado-coordenador',
}))`
  width: 16px;
  height: 16px;
  stroke: #8b0000;
  stroke-width: 1.5;
  cursor: pointer;
`;

const TextoCelula = styled.span.attrs(() => ({ className: 'texto-celula' }))`
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  font-weight: normal;
  color: ${({ status }) => {
    if (status === 'Confirmado') return '#24A788';
    if (status === 'Pendente') return '#9D9D09';
    if (status === 'Cancelado') return '#A72424';
    if (status === 'Cancelado_Coordenador') return '#8B0000';
    if (status === 'Aguardando_Cancelamento') return '#C27C0E';
    return '#333';
  }};
  white-space: normal;
  word-wrap: break-word;
  align-self: flex-start;
`;

const TextoAcao = styled.span`
  color: #222;
  cursor: pointer;
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  background: none;
  border: none;
  padding: 0;
  text-decoration: none;
  transition: color 0.2s;
  &:hover {
    color: #008080;
  }
`;

const BotaoCancelar = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 6px 14px;
  border: none;
  border-radius: 6px;
  background-color: #a72424;
  color: #fff;
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #8b1e1e;
  }
`;

const AcaoComIcone = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const ContainerStatus = styled.div.attrs(() => ({
  className: 'container-status',
}))`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const IconeAcaoSubstituir = styled.img`
  width: 13px;
  height: 13px;
  vertical-align: middle;
  padding-right: 2px;
  padding-left: 3px;
`;

const IconeAcaoEnviarConfirmacao = styled.img`
  width: 18px;
  height: 18px;
  vertical-align: middle;
`;

const ListaUsuarios = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CabecalhoData = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
`;

const ListaHorarios = styled.div`
  margin-top: 4px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const TextoHorario = styled(TextoCelula)`
  font-size: 13px;
  color: #666666;
  margin: 0;
  padding: 0;
  width: 100%;
  text-align: left;
`;

const Tabela = ({
  designacoes,
  selectedItems,
  selectAll,
  onSelectItem,
  onSelectAll,
  onDelete,
  onRequestCancelamento,
  carregando,
  onRefresh,
}) => {
  const [ordenacao, setOrdenacao] = useState('asc');
  const [idLinhaAberta, setIdLinhaAberta] = useState(null);
  const [snackbar, setSnackbar] = useState(null);

  const isSecretaria = useMemo(() => {
    try {
      const perfis = JSON.parse(localStorage.getItem('perfilUsuario') || '[]');
      return perfis.includes('secretaria') && !perfis.includes('administrador');
    } catch {
      return false;
    }
  }, []);

  const formatarMensagem = useCallback((nomePublicador, dataDesignacao, nomePonto) => {
    let dataStr = '';
    try {
      const d = new Date(dataDesignacao.split('T')[0] + 'T12:00:00');
      dataStr = d.toLocaleDateString('pt-BR');
    } catch (e) {
      dataStr = dataDesignacao || '';
    }

    const linkApp = "https://tpedf.com.br/app.html"; 

    return `Olá, ${nomePublicador}!\n\nEsta é uma mensagem automática do TPE-DF sobre sua designação para o dia ${dataStr}, no ponto ${nomePonto}.\n\nPor gentileza, acesse o aplicativo do TPE-DF para confirmar ou cancelar sua designação.\n\n*Importante:* respostas por WhatsApp não confirmam nem cancelam sua participação. Sua confirmação ou cancelamento só serão registrados quando realizados diretamente no aplicativo.\n\nAgradecemos sua atenção!`;
  }, []);

  const gerarLinkWhatsApp = useCallback((publicador, designacao) => {
    const telefonePublicador = publicador?.telefone || '';
    let numero = telefonePublicador.replace(/\D/g, '');

    if (!numero) {
      setSnackbar({ mensagem: 'Este publicador não possui telefone cadastrado.', tipo: 'error' });
      return;
    }

    // Garante o código do país Brasil (55) caso não esteja presente
    if (!numero.startsWith('55')) {
      numero = '55' + numero;
    }

    const nome = publicador?.nome || '';
    const data = designacao?.data || '';
    const ponto = designacao?.ponto?.nome || '';
    const mensagem = formatarMensagem(nome, data, ponto);
    const link = `https://wa.me/${numero}?text=${encodeURIComponent(mensagem)}`;
    window.open(link, '_blank');
  }, [formatarMensagem]);

  const designacoesFiltradas = useMemo(() => {
    if (!designacoes) return [];

    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);

    return designacoes.filter((designacao) => {
      if (!designacao?.data) return false;

      const dataDesignacao = new Date(
        designacao.data.split('T')[0] + 'T00:00:00'
      );
      return dataDesignacao >= hoje;
    });
  }, [designacoes]);

  const designacoesOrdenadas = useMemo(() => (designacoesFiltradas || [])
    .slice()
    .sort((a, b) => {
      if (!a?.data) return 1;
      if (!b?.data) return -1;
      const aDate = new Date(a.data.split('T')[0] + 'T00:00:00').getTime();
      const bDate = new Date(b.data.split('T')[0] + 'T00:00:00').getTime();
      return ordenacao === 'asc' ? aDate - bDate : bDate - aDate;
    }), [designacoesFiltradas, ordenacao]);

  const obterStatusTabela = (designacaoAtual) => {
    const statusGeral = designacaoAtual?.status;

    const statusIndividuais = (designacaoAtual?.publicadores || []).map(
      (publicador) => publicador?.statusIndividual || 'Pendente'
    );

    const totalPublicadores = statusIndividuais.length;

    const confirmados = statusIndividuais.filter(
      (status) => status === 'Confirmado'
    ).length;
    const cancelados = statusIndividuais.filter(
      (status) => status === 'Cancelado' || status === 'Cancelado_Coordenador'
    ).length;
    const pendentes = statusIndividuais.filter(
      (status) => status === 'Pendente'
    ).length;

    // REGRAS DE NEGÓCIO:

    // 1) Com 3 publicadores:
    //    - Se 2 ou mais cancelaram -> Cancelado (independente dos demais)
    //    - Se 2 ou mais confirmaram -> Confirmado (independente do terceiro)
    if (totalPublicadores === 3) {
      if (cancelados >= 2) {
        return 'Cancelado';
      }
      if (confirmados >= 2) {
        return 'Confirmado';
      }
      // Se apenas 1 confirmou e 1 cancelou (1 pendente) -> Pendente
      if (confirmados === 1 && cancelados === 1 && pendentes === 1) {
        return 'Pendente';
      }
      // Demais casos (ex: todos pendentes, 1 confirmado e 2 pendentes, etc.) -> Pendente
      return 'Pendente';
    }

    // 2) Com 2 publicadores:
    //    - Se ambos confirmaram -> Confirmado
    //    - Se ambos cancelaram -> Cancelado
    //    - Se um confirmou e outro cancelou/pendente -> Cancelado
    //    - Se nenhum confirmou -> Pendente
    if (totalPublicadores === 2) {
      if (confirmados === 2) {
        return 'Confirmado';
      }
      if (cancelados >= 1) {
        return 'Cancelado';
      }
      // Ambos pendentes
      return 'Pendente';
    }

    // 3) Fallback para outros casos (1 publicador ou > 3):
    //    - Todos cancelaram -> Cancelado
    //    - Pelo menos 2 confirmaram -> Confirmado
    //    - 1 confirmado e os demais cancelaram -> Aguardando_Cancelamento
    //    - Caso contrário -> Pendente
    if (totalPublicadores > 0 && cancelados === totalPublicadores) {
      return 'Cancelado';
    }

    if (confirmados >= 2) {
      return 'Confirmado';
    }

    if (confirmados === 1 && cancelados >= 1 && pendentes === 0) {
      return 'Aguardando_Cancelamento';
    }

    // Se status geral for válido, usa ele como fallback
    if (
      [
        'Cancelado_Coordenador',
        'Cancelado',
        'Confirmado',
        'Aguardando_Cancelamento',
        'Pendente',
      ].includes(statusGeral)
    ) {
      return statusGeral;
    }

    return 'Pendente';
  };

  return (
    <ContainerTabela>
      <WrapperTabela>
        <CabecalhoSticky>
          <CelulaCabecalho center title={isSecretaria ? 'Perfil de acesso não pode excluir designações' : ''}>
            <CaixaSelecao 
              checked={selectAll} 
              onChange={onSelectAll} 
              disabled={isSecretaria}
              style={{ cursor: isSecretaria ? 'not-allowed' : 'pointer' }}
            />
          </CelulaCabecalho>
          <CelulaCabecalho>
            <CabecalhoData>
              <IconeOrdenacao
                viewBox='0 0 24 24'
                xmlns='http://www.w3.org/2000/svg'
                onClick={() =>
                  setOrdenacao(ordenacao === 'asc' ? 'desc' : 'asc')
                }
              >
                <path
                  d='M7.5 17.5V6.5M7.5 6.5L11 10M7.5 6.5L4 10M16.5 6.5V17.5M16.5 17.5L20 14M16.5 17.5L13 14'
                  stroke='#F1F7F9'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </IconeOrdenacao>
              Data
            </CabecalhoData>
          </CelulaCabecalho>
          <CelulaCabecalho>Companheiros</CelulaCabecalho>
          <CelulaCabecalho>Ponto</CelulaCabecalho>
          <CelulaCabecalho>Ação</CelulaCabecalho>
          <CelulaCabecalho>Status</CelulaCabecalho>
          <CelulaCabecalho />
        </CabecalhoSticky>
        <CorpoTabela>
          {carregando ? (
            <EstadoCarregando />
          ) : designacoesOrdenadas && designacoesOrdenadas.length === 0 ? (
            <EstadoVazio />
          ) : (
            designacoesOrdenadas?.map((designacao, index) => {
              const statusExibicao = obterStatusTabela(designacao);
              return (
<LinhaTabela key={index}>
  <CelulaTabela center title={isSecretaria ? 'Perfil de acesso não pode excluir designações' : ''}>
    <CaixaSelecao
      checked={selectedItems.includes(designacao?._id)}
      onChange={() => onSelectItem(designacao?._id)}
      disabled={isSecretaria}
      style={{ cursor: isSecretaria ? 'not-allowed' : 'pointer' }}
    />
  </CelulaTabela>

  <CelulaTabela center>
    <TextoCelula>
      {designacao?.data
        ? new Date(designacao.data.split('T')[0] + 'T12:00:00').toLocaleDateString('pt-BR')
        : ''}
    </TextoCelula>
    {designacao?.horario && Array.isArray(designacao.horario) && designacao.horario.length > 0 && (
      <ListaHorarios>
        {designacao.horario.map((h, idx) => (
          <TextoHorario key={idx}>{h}</TextoHorario>
        ))}
      </ListaHorarios>
    )}
  </CelulaTabela>

  <CelulaTabela centerVertical>
    {designacao?.publicadores?.map((publicador, index) => (
      <StatusPublicador key={index} publicador={publicador} />
    ))}
  </CelulaTabela>

  <CelulaTabela centerVertical>
    <TextoCelula>{designacao?.ponto?.nome}</TextoCelula>
    <TextoCelula>
      Endereço: {designacao?.ponto?.local}
    </TextoCelula>
  </CelulaTabela>

  <CelulaTabela centerVertical>
    <div style={{ position: 'relative' }}>
      {designacao?.publicadores?.map((publicador, index) => {
        const estaCancelado =
          publicador?.statusIndividual === 'Cancelado' ||
          publicador?.statusIndividual === 'Cancelado_Coordenador';

        return (
          <div key={index} style={{ marginBottom: '5px', minHeight: '20px' }}>
          {estaCancelado && featureFlags.SUBSTITUIR_PUBLICADOR && !isSecretaria ? (
            <TextoAcao
              onClick={() => {
                if (idLinhaAberta === designacao._id) {
                    setIdLinhaAberta(null);
                } else {
                    setIdLinhaAberta(designacao._id);
                }
              }}
            >
              <AcaoComIcone>
                <IconeSubstituirSvg viewBox="0 0 17 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6.9805 2.11051C8.35665 2.11051 9.60317 2.70145 10.5106 3.65118L8.82534 5.43456C8.51621 5.76169 8.73559 6.33153 9.18434 6.33153H13.4624C13.7416 6.33153 13.961 6.09937 13.961 5.8039V1.27686C13.961 0.801993 13.4225 0.569837 13.1134 0.907519L11.9167 2.17382C10.6502 0.823098 8.90512 0 6.9805 0C3.94897 0 1.37616 2.04719 0.41883 4.89638C0.179499 5.59285 0.648189 6.33153 1.34624 6.33153C1.76507 6.33153 2.13404 6.05716 2.27365 5.63506C2.95175 3.58786 4.80657 2.11051 6.9805 2.11051ZM12.6048 11.7555C13.0037 11.1857 13.3228 10.542 13.5521 9.85608C13.7815 9.17016 13.3028 8.44203 12.6148 8.44203C12.1959 8.44203 11.827 8.7164 11.6873 9.1385C11.0092 11.1857 9.15442 12.6631 6.9805 12.6631C5.60434 12.6631 4.35782 12.0721 3.45036 11.1224L5.13565 9.339C5.44479 9.01187 5.2254 8.44203 4.77665 8.44203H0.498607C0.219387 8.44203 0 8.67419 0 8.96966V13.4967C0 13.9716 0.538495 14.2037 0.847632 13.866L2.04429 12.5997C3.31075 13.9505 5.05587 14.7736 6.9805 14.7736C8.52618 14.7736 9.95219 14.2354 11.109 13.3384L15.2075 17.6755C15.6164 18.1082 16.2845 18.1082 16.6934 17.6755C17.1022 17.2429 17.1022 16.5358 16.6934 16.1032L12.6048 11.7555Z" />
                </IconeSubstituirSvg>
                Substituir Publicador
              </AcaoComIcone>
            </TextoAcao>
          ) : (
             <TextoAcao onClick={() => gerarLinkWhatsApp(publicador, designacao)}>
                <AcaoComIcone>
                    <IconeAcaoEnviarConfirmacao src={enviarConfirmacaoIcon} alt='Enviar Confirmação' />
                    Enviar Confirmação
                </AcaoComIcone>
             </TextoAcao>
          )}
          </div>
        );
      })}
      {idLinhaAberta === designacao._id && (
        <ModalSubstituicao 
          designacao={designacao}
          onClose={() => setIdLinhaAberta(null)}
          onRefresh={onRefresh}
          designacoesCarregadas={designacoesOrdenadas}
        />
      )}
    </div>
  </CelulaTabela>

  <CelulaTabela centerVertical>
    <ContainerStatus>
      {statusExibicao === 'Confirmado' && (
        <>
          <IconeConfirmado xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none'>
             <path d='M21 12C21 13.1819 20.7672 14.3522 20.3149 15.4442C19.8626 16.5361 19.1997 17.5282 18.364 18.364C17.5282 19.1997 16.5361 19.8626 15.4442 20.3149C14.3522 20.7672 13.1819 21 12 21C10.8181 21 9.64778 20.7672 8.55585 20.3149C7.46392 19.8626 6.47177 19.1997 5.63604 18.364C4.80031 17.5282 4.13738 16.5361 3.68508 15.4442C3.23279 14.3522 3 13.1819 3 12C3 9.61305 3.94821 7.32387 5.63604 5.63604C7.32387 3.94821 9.61305 3 12 3C14.3869 3 16.6761 3.94821 18.364 5.63604C20.0518 7.32387 21 9.61305 21 12Z' stroke='#24A788' strokeLinecap='round' strokeLinejoin='round' />
             <path d='M8.66699 12.633L10.172 14.354C10.271 14.4673 10.3944 14.5568 10.5328 14.6159C10.6713 14.6749 10.8213 14.7019 10.9716 14.6949C11.1219 14.6879 11.2688 14.647 11.4011 14.5753C11.5335 14.5037 11.6479 14.4031 11.736 14.281L15.333 9.29999' stroke='#24A788' strokeLinecap='round' strokeLinejoin='round' />
          </IconeConfirmado>
          <TextoCelula status='Confirmado'>Confirmado</TextoCelula>
        </>
      )}
      {statusExibicao === 'Cancelado' && (
        <>
          <IconeCancelado xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none'>
            <path d='M15 9L9 15M9 9L15 15M21 12C21 13.1819 20.7672 14.3522 20.3149 15.4442C19.8626 16.5361 19.1997 17.5282 18.364 18.364C17.5282 19.1997 16.5361 19.8626 15.4442 20.3149C14.3522 20.7672 13.1819 21 12 21C10.8181 21 9.64778 20.7672 8.55585 20.3149C7.46392 19.8626 6.47177 19.1997 5.63604 18.364C4.80031 17.5282 4.13738 16.5361 3.68508 15.4442C3.23279 14.3522 3 13.1819 3 12C3 9.61305 3.94821 7.32387 5.63604 5.63604C7.32387 3.94821 9.61305 3 12 3C14.3869 3 16.6761 3.94821 18.364 5.63604C20.0518 7.32387 21 9.61305 21 12Z' stroke='#A72424' strokeLinecap='round' strokeLinejoin='round' />
          </IconeCancelado>
          <TextoCelula status='Cancelado'>Cancelado</TextoCelula>
        </>
      )}
      {statusExibicao === 'Cancelado_Coordenador' && (
         <>
          <IconeCanceladoCoordenador xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none'>
            <path d='M15 9L9 15M9 9L15 15M21 12C21 13.1819 20.7672 14.3522 20.3149 15.4442C19.8626 16.5361 19.1997 17.5282 18.364 18.364C17.5282 19.1997 16.5361 19.8626 15.4442 20.3149C14.3522 20.7672 13.1819 21 12 21C10.8181 21 9.64778 20.7672 8.55585 20.3149C7.46392 19.8626 6.47177 19.1997 5.63604 18.364C4.80031 17.5282 4.13738 16.5361 3.68508 15.4442C3.23279 14.3522 3 13.1819 3 12C3 9.61305 3.94821 7.32387 5.63604 5.63604C7.32387 3.94821 9.61305 3 12 3C14.3869 3 16.6761 3.94821 18.364 5.63604C20.0518 7.32387 21 9.61305 21 12Z' stroke='#8B0000' strokeLinecap='round' strokeLinejoin='round' />
          </IconeCanceladoCoordenador>
          <TextoCelula status='Cancelado_Coordenador'>Cancelado</TextoCelula>
         </>
      )}
      {statusExibicao === 'Aguardando_Cancelamento' && (
        <BotaoCancelar type='button' onClick={() => onRequestCancelamento && onRequestCancelamento(designacao)}>
          Cancelar
        </BotaoCancelar>
      )}
      {statusExibicao === 'Pendente' && (
        <>
          <IconePendente xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none'>
             <path d='M21 12C21 13.1819 20.7672 14.3522 20.3149 15.4442C19.8626 16.5361 19.1997 17.5282 18.364 18.364C17.5282 19.1997 16.5361 19.8626 15.4442 20.3149C14.3522 20.7672 13.1819 21 12 21C10.8181 21 9.64778 20.7672 8.55585 20.3149C7.46392 19.8626 6.47177 19.1997 5.63604 18.364C4.80031 17.5282 4.13738 16.5361 3.68508 15.4442C3.23279 14.3522 3 13.1819 3 12C3 9.61305 3.94821 7.32387 5.63604 5.63604C7.32387 3.94821 9.61305 3 12 3C14.3869 3 16.6761 3.94821 18.364 5.63604C20.0518 7.32387 21 9.61305 21 12Z' stroke='#9D9D09' strokeLinecap='round' strokeLinejoin='round' />
             <path d='M12 16V11H11.5M11.5 16H12.5M12 8.5V8' stroke='#9D9D09' strokeLinecap='round' strokeLinejoin='round' />
          </IconePendente>
          <TextoCelula status='Pendente'>Pendente</TextoCelula>
        </>
      )}
    </ContainerStatus>
  </CelulaTabela>

  <CelulaIcone title={isSecretaria ? 'Perfil de acesso não pode excluir designações' : ''}>
    <IconeLixeira 
       xmlns='http://www.w3.org/2000/svg' 
       viewBox='0 0 32 32' 
       fill='none' 
       onClick={() => { if (!isSecretaria) onDelete(designacao?._id); }}
       style={{ opacity: isSecretaria ? 0.3 : 1, cursor: isSecretaria ? 'not-allowed' : 'pointer' }}
    >
       <path d='M5.33337 9.33334H26.6667' stroke='#DB1212' strokeWidth='1.57' strokeLinecap='round' strokeLinejoin='round' />
       <path d='M13.3333 14.6667V22.6667' stroke='#DB1212' strokeWidth='1.57' strokeLinecap='round' strokeLinejoin='round' />
       <path d='M18.6667 14.6667V22.6667' stroke='#DB1212' strokeWidth='1.57' strokeLinecap='round' strokeLinejoin='round' />
       <path d='M6.66675 9.33334L8.00008 25.3333C8.00008 26.0406 8.28103 26.7189 8.78113 27.219C9.28123 27.7191 9.9595 28 10.6667 28H21.3334C22.0407 28 22.7189 27.7191 23.219 27.219C23.7191 26.7189 24.0001 26.0406 24.0001 25.3333L25.3334 9.33334' stroke='#DB1212' strokeWidth='1.57' strokeLinecap='round' strokeLinejoin='round' />
       <path d='M12 9.33333V5.33333C12 4.97971 12.1405 4.64057 12.3905 4.39052C12.6406 4.14048 12.9797 4 13.3333 4H18.6667C19.0203 4 19.3594 4.14048 19.6095 4.39052C19.8595 4.64057 20 4.97971 20 5.33333V9.33333' stroke='#DB1212' strokeWidth='1.57' strokeLinecap='round' strokeLinejoin='round' />
    </IconeLixeira>
  </CelulaIcone>
</LinhaTabela>
              );
            })
          )}
        </CorpoTabela>
      </WrapperTabela>
      {snackbar && (
        <Snackbar
          mensagem={snackbar.mensagem}
          tipo={snackbar.tipo}
          onClose={() => setSnackbar(null)}
        />
      )}
    </ContainerTabela>
  );
};

export default React.memo(Tabela);
